import { useEffect, useRef, useState } from 'react';
import { useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api';
export default function LiveViewer({ deviceId, onClose }: { deviceId: string; onClose: () => void }) {
  const start = useAction((api as any).plix.startLive), heartbeat = useAction((api as any).plix.keepLive), close = useAction((api as any).plix.closeLive);
  const video = useRef<HTMLVideoElement>(null);
  const [message, setMessage] = useState('Connecting to camera…');
  useEffect(() => {
    let cancelled = false, viewerId: string | null = null, timer: ReturnType<typeof setTimeout> | undefined;
    let destroy: (() => void) | undefined;
    const element = video.current!;
    const release = () => { if (viewerId) { const id = viewerId; viewerId = null; void close({ viewerId: id }).catch(() => {}); } };
    const beat = async () => {
      if (cancelled || !viewerId) return;
      try { await heartbeat({ viewerId, playing: !element.paused && element.readyState >= 3 }); if (!cancelled) timer = setTimeout(beat, 10000); }
      catch { if (!cancelled) { setMessage('Live connection interrupted. Close the viewer and retry.'); element.pause(); destroy?.(); release(); } }
    };
    const begin = async () => {
      try {
        const result = await start({ deviceId });
        if (cancelled) { void close({ viewerId: result.viewerId }).catch(() => {}); return; }
        viewerId = result.viewerId;
        if (element.canPlayType('application/vnd.apple.mpegurl')) element.src = result.url;
        else {
          const { default: Hls } = await import('hls.js');
          if (cancelled) return;
          if (!Hls.isSupported()) throw new Error('Unsupported live playback');
          const hls = new Hls({ lowLatencyMode: true, backBufferLength: 20, maxBufferLength: 10 });
          destroy = () => hls.destroy();
          hls.on(Hls.Events.ERROR, (_, data) => { if (data.fatal && !cancelled) { setMessage('Live video is unavailable. Close and retry.'); clearTimeout(timer); destroy?.(); release(); } });
          hls.loadSource(result.url); hls.attachMedia(element);
        }
        if (!cancelled) { setMessage('Live · muted'); void element.play().catch(() => setMessage('Press play to watch live.')); timer = setTimeout(beat, 10000); }
      } catch { if (!cancelled) { setMessage('Could not open the live stream. Check the camera and connection.'); release(); } }
    };
    // Avoid duplicate start commands from React development StrictMode mounting.
    const beginTimer = setTimeout(begin, 0);
    return () => { cancelled = true; clearTimeout(beginTimer); clearTimeout(timer); element.pause(); destroy?.(); element.removeAttribute('src'); element.load(); release(); };
  }, [deviceId, start, heartbeat, close]);
  return <div className="bodycam-live"><div className="bodycam-section-label"><span>Live bodycam</span><button onClick={onClose}>Close viewer</button></div><video ref={video} controls muted playsInline style={{ width: '100%', background: '#000' }}/><p className="bodycam-note" role="status">{message}</p></div>;
}
